import { AppointmentType } from "@accurx/api/patient-messaging";
import { Log } from "@accurx/shared";

import { SLUGS_GP_BATCH } from "shared/Routes";

import { BatchType } from "./BatchMessage.types";

const ALLOWED_STAGGER = [BatchType.MESSAGE, BatchType.FLOREY];

enum BatchRoute {
    COMPOSE = "Compose",
    UPLOAD = "Upload",
    SCHEDULE = "Schedule",
    REVIEW = "Review",
}

const findBatchRoute = (
    currentRoute: BatchRoute,
    batchType?: BatchType,
): { back: string; forward: string } => {
    let backRoute = "";
    let forwardRoute = "";

    switch (currentRoute) {
        case BatchRoute.COMPOSE:
            backRoute = SLUGS_GP_BATCH.choose;
            forwardRoute = SLUGS_GP_BATCH.upload;
            break;
        case BatchRoute.UPLOAD:
            if (!batchType) {
                Log.error("You forgot to pass batchType or isFeatureStagger");
                break;
            }
            switch (batchType) {
                case BatchType.COPD:
                    backRoute = SLUGS_GP_BATCH.composeCopd;
                    break;
                case BatchType.MESSAGE:
                    backRoute = SLUGS_GP_BATCH.composeSms;
                    break;
                case BatchType.SELFBOOK:
                    backRoute = SLUGS_GP_BATCH.composeSelfbook;
                    break;
                case BatchType.FLOREY:
                    backRoute = SLUGS_GP_BATCH.composeFlorey;
                    break;
                case BatchType.NONE:
                default:
                    backRoute = SLUGS_GP_BATCH.choose;
                    break;
            }
            forwardRoute = ALLOWED_STAGGER.includes(batchType)
                ? SLUGS_GP_BATCH.schedule
                : SLUGS_GP_BATCH.review;
            break;
        case BatchRoute.SCHEDULE:
            backRoute = SLUGS_GP_BATCH.upload;
            forwardRoute = SLUGS_GP_BATCH.review;
            break;
        case BatchRoute.REVIEW:
            if (!batchType) {
                Log.error("You forgot to pass batchType or isFeatureStagger");
                break;
            }
            backRoute = ALLOWED_STAGGER.includes(batchType)
                ? SLUGS_GP_BATCH.schedule
                : SLUGS_GP_BATCH.upload;
            // forward - different possible routes handle by the page
            break;
        default:
            break;
    }

    return {
        back: backRoute,
        forward: forwardRoute,
    };
};

/**
 * Only Florey and SMS flows have the stagger feature at the moment
 */
const TOTAL_STEPS_WITHOUT_STAGGER = 3;
const getTotalStep = (batchType: BatchType): number => {
    let totalStep = TOTAL_STEPS_WITHOUT_STAGGER;
    if (ALLOWED_STAGGER.includes(batchType)) {
        totalStep += 1;
    }
    return totalStep;
};

const displayBatchType = (type: string, description: string): string => {
    if (type === "FLOREY") {
        return `${BatchType.FLOREY} - ${description}`;
    }
    if (type === "SELFBOOK") {
        return BatchType.SELFBOOK;
    }
    if (type === "SMS") {
        return BatchType.MESSAGE;
    }
    return description;
};

const displayAppointmentType = (type: AppointmentType): string => {
    switch (type) {
        case AppointmentType.Telephone:
            return "Telephone";
        case AppointmentType.FaceToFace:
        default:
            return "Face to face";
    }
};

const SELF_BOOK_EXPIRY = 7;

const getSelfBookLinkExpiryDays = (): number => {
    return SELF_BOOK_EXPIRY;
};

export {
    BatchRoute,
    findBatchRoute,
    getTotalStep,
    displayBatchType,
    displayAppointmentType,
    getSelfBookLinkExpiryDays,
};

const SELF_BOOK_PROMOTION_HOW_IT_WORKS =
    "accurx-self-book-promotion-how-it-works";
const SELF_BOOK_PROMOTION_I_AM_NOT_INTERESTED =
    "accurx-self-book-promotion-i-am-not-interested";

export const updateShowMeHowItWorksCount = (userId: string, counter: string) =>
    window.localStorage.setItem(
        `${SELF_BOOK_PROMOTION_HOW_IT_WORKS}-${userId}`,
        counter,
    );

export const getShowMeHowItWorksCount = (userId: string) =>
    window.localStorage.getItem(
        `${SELF_BOOK_PROMOTION_HOW_IT_WORKS}-${userId}`,
    ) ?? "0";

export const updateIamNotInterestedCount = (userId: string, counter: string) =>
    window.localStorage.setItem(
        `${SELF_BOOK_PROMOTION_I_AM_NOT_INTERESTED}-${userId}`,
        counter,
    );

export const getIamNotInterestedCount = (userId: string) =>
    window.localStorage.getItem(
        `${SELF_BOOK_PROMOTION_I_AM_NOT_INTERESTED}-${userId}`,
    ) ?? "0";
